// src/features/Analytics/mlPredictorTF.js
import * as tf from '@tensorflow/tfjs';
import { MLDataPreprocessor } from '../../utils/mlDataPreprocessor';

export class MLPredictorTF {
  constructor() {
    this.trainingData = [];
    this.model = null;
    this.isUsingTF = false;
    this.modelMetrics = {
      accuracy: 0,
      lastTraining: null,
      dataPoints: 0,
      modelType: 'simple'
    };
    
    this.tf = tf;
  }

  // Ініціалізація TensorFlow.js
  async initializeTF() {
    try {
      // Перевіряємо доступність TensorFlow.js
      if (typeof this.tf === 'undefined' || !this.tf.sequential) {
        console.warn('TensorFlow.js not available, falling back to simple model');
        this.isUsingTF = false;
        return false;
      }

      // Отримуємо інформацію про backend
      const backend = await this.tf.getBackend();
      console.log(`TensorFlow.js initialized with backend: ${backend}`);
      
      this.isUsingTF = true;
      return true;
    } catch (error) {
      console.warn('TensorFlow.js initialization failed:', error);
      this.isUsingTF = false;
      return false;
    }
  }

  // Додавання тренувальних даних
  addTrainingData(records) {
    if (!Array.isArray(records)) {
      console.warn('MLPredictorTF: Expected array of records');
      return;
    }

    const processedRecords = records
      .map(record => MLDataPreprocessor.normalizeRecord(record))
      .filter(record => record !== null && record.dataQuality.hasProduction);

    console.log(`MLPredictorTF: Processed ${processedRecords.length} of ${records.length} records`);
    
    this.trainingData.push(...processedRecords);
    this.modelMetrics.dataPoints = this.trainingData.length;
  }

  // Тренування моделі з TensorFlow.js
  async trainProductionModel() {
    if (this.trainingData.length < 10) {
      console.warn('MLPredictorTF: Insufficient data for TensorFlow model. Need at least 10 records.');
      return this.trainSimpleModel();
    }

    // Спробуємо використати TensorFlow.js
    const tfAvailable = await this.initializeTF();
    
    if (!tfAvailable || this.trainingData.length < 20) {
      console.log('MLPredictorTF: Using simple model (TF not available or insufficient data)');
      return this.trainSimpleModel();
    }

    try {
      console.log(`MLPredictorTF: Training TensorFlow model with ${this.trainingData.length} records...`);
      
      // Підготовка даних для TensorFlow
      const { features, targets, featureStats } = this.prepareTFData();
      
      // Створення моделі
      this.model = this.createTFModel(features.shape[1]);
      
      // Тренування моделі
      const history = await this.model.fit(features, targets, {
        epochs: 100,
        batchSize: 32,
        validationSplit: 0.2,
        verbose: 0,
        callbacks: {
          onEpochEnd: (epoch, logs) => {
            if (epoch % 20 === 0) {
              console.log(`Epoch ${epoch}: loss = ${logs.loss.toFixed(4)}, val_loss = ${logs.val_loss.toFixed(4)}`);
            }
          }
        }
      });

      // Оцінка моделі
      const accuracy = await this.evaluateTFModel(features, targets);
      
      this.modelMetrics.accuracy = accuracy;
      this.modelMetrics.lastTraining = new Date();
      this.modelMetrics.modelType = 'tensorflow_sequential';
      this.modelMetrics.featureStats = featureStats;

      console.log(`MLPredictorTF: TensorFlow model trained! Accuracy: ${accuracy.toFixed(1)}%`);
      
      // Очищення пам'яті
      features.dispose();
      targets.dispose();
      
      return this.model;

    } catch (error) {
      console.error('MLPredictorTF: TensorFlow training failed, falling back to simple model:', error);
      return this.trainSimpleModel();
    }
  }

  // Створення моделі TensorFlow
  createTFModel(inputDim) {
    const model = this.tf.sequential();
    
    // Приховані шари
    model.add(this.tf.layers.dense({
      units: 64,
      activation: 'relu',
      inputShape: [inputDim]
    }));
    
    model.add(this.tf.layers.dropout({ rate: 0.3 }));
    
    model.add(this.tf.layers.dense({
      units: 32,
      activation: 'relu'
    }));
    
    model.add(this.tf.layers.dropout({ rate: 0.2 }));
    
    model.add(this.tf.layers.dense({
      units: 16,
      activation: 'relu'
    }));
    
    // Вихідний шар
    model.add(this.tf.layers.dense({
      units: 1,
      activation: 'linear'
    }));

    // Компіляція моделі
    model.compile({
      optimizer: this.tf.train.adam(0.001),
      loss: 'meanSquaredError',
      metrics: ['mae']
    });

    return model;
  }

  // Підготовка даних для TensorFlow
  prepareTFData() {
    const sortedData = [...this.trainingData].sort((a, b) => a.timestamp - b.timestamp);
    
    const featuresArray = [];
    const targetsArray = [];
    
    // Збір статистики для нормалізації
    const featureStats = {
      min: Array(9).fill(Number.MAX_VALUE),
      max: Array(9).fill(Number.MIN_VALUE),
      mean: Array(9).fill(0)
    };

    sortedData.forEach(record => {
      const featureVector = this.extractFeatures(record);
      featuresArray.push(featureVector);
      targetsArray.push(record.productionFeatures.totalProduction);
      
      // Оновлення статистики
      featureVector.forEach((value, index) => {
        featureStats.min[index] = Math.min(featureStats.min[index], value);
        featureStats.max[index] = Math.max(featureStats.max[index], value);
        featureStats.mean[index] += value;
      });
    });

    // Обчислення середніх значень
    featureStats.mean = featureStats.mean.map(sum => sum / featuresArray.length);

    // Нормалізація ознак
    const normalizedFeatures = featuresArray.map(featureVector =>
      featureVector.map((value, index) => {
        const range = featureStats.max[index] - featureStats.min[index];
        return range > 0 ? (value - featureStats.mean[index]) / range : 0;
      })
    );

    const features = this.tf.tensor2d(normalizedFeatures);
    const targets = this.tf.tensor1d(targetsArray);

    return { features, targets, featureStats };
  }

  // Витяг ознак для TensorFlow
  extractFeatures(record) {
    return [
      record.timeFeatures.dayOfWeek / 6,                    // Нормалізований день тижня
      record.timeFeatures.month / 11,                      // Нормалізований місяць
      record.timeFeatures.isWeekend,                       // Вихідний (0 або 1)
      record.timeFeatures.season / 3,                      // Нормалізований сезон
      record.shiftFeatures.shiftType,                      // Тип зміни (0 або 1)
      record.shiftFeatures.operatorsCount / 5,             // Нормалізована кількість операторів
      record.shiftFeatures.shiftDuration / 24,             // Нормалізована тривалість зміни
      record.productionFeatures.activeProgramsCount / 10,  // Нормалізована кількість програм
      record.productionFeatures.rejectionRate / 100        // Нормалізований відсоток браку
    ];
  }

  // Оцінка моделі TensorFlow
  async evaluateTFModel(features, targets) {
    try {
      const predictions = this.model.predict(features);
      const loss = this.tf.metrics.meanSquaredError(targets, predictions);
      
      const lossValue = await loss.data();
      const avgTarget = this.tf.mean(targets).dataSync()[0];
      
      const accuracy = Math.max(0, 100 * (1 - Math.sqrt(lossValue[0]) / avgTarget));
      
      // Очищення пам'яті
      predictions.dispose();
      loss.dispose();
      
      return accuracy;
    } catch (error) {
      console.error('MLPredictorTF: Model evaluation failed:', error);
      return 70; // Консервативна оцінка
    }
  }

  // Резервна проста модель
  trainSimpleModel() {
    console.log('MLPredictorTF: Training simple model...');
    
    const { features, targets } = this.prepareTrainingData();
    
    // Проста лінійна регресія
    this.model = this.simpleLinearRegression(features, targets);
    
    // Розрахунок точності
    this.modelMetrics.accuracy = this.calculateModelAccuracy(features, targets);
    this.modelMetrics.lastTraining = new Date();
    this.modelMetrics.modelType = 'simple_linear';
    
    console.log(`MLPredictorTF: Simple model trained! Accuracy: ${this.modelMetrics.accuracy.toFixed(1)}%`);
    
    return this.model;
  }

  // Прогнозування з TensorFlow
  async predictProduction(days = 7) {
    if (!this.model) {
      console.log('MLPredictorTF: No model available, using fallback prediction');
      return this.fallbackPrediction(days);
    }

    try {
      const predictions = [];
      const lastRecord = this.trainingData[this.trainingData.length - 1];
      const lastDate = new Date(lastRecord.date);

      for (let i = 1; i <= days; i++) {
        const predictionDate = new Date(lastDate);
        predictionDate.setDate(predictionDate.getDate() + i);

        const prediction = await this.predictSingleDayTF(predictionDate, lastRecord);
        
        predictions.push({
          date: predictionDate.toISOString().split('T')[0],
          predictedProduction: Math.round(prediction),
          confidence: this.calculateTFConfidence(i),
          modelUsed: this.modelMetrics.modelType,
          features: this.getPredictionFeatures(predictionDate, lastRecord)
        });
      }

      return predictions;

    } catch (error) {
      console.error('MLPredictorTF: TensorFlow prediction failed, using fallback:', error);
      return this.fallbackPrediction(days);
    }
  }

  // Прогнозування одного дня з TensorFlow
  async predictSingleDayTF(date, lastRecord) {
    if (!this.isUsingTF || !this.model) {
      return this.predictSingleDaySimple(date, lastRecord);
    }

    try {
      const features = this.extractFeaturesForPrediction(date, lastRecord);
      const normalizedFeatures = this.normalizeFeatures(features, this.modelMetrics.featureStats);
      
      const input = this.tf.tensor2d([normalizedFeatures]);
      const prediction = this.model.predict(input);
      const predictionValue = prediction.dataSync()[0];
      
      // Очищення пам'яті
      input.dispose();
      prediction.dispose();
      
      return Math.max(0, predictionValue);
      
    } catch (error) {
      console.error('MLPredictorTF: Single day prediction failed:', error);
      return this.predictSingleDaySimple(date, lastRecord);
    }
  }

  // Нормалізація ознак для прогнозування
  normalizeFeatures(features, stats) {
    return features.map((value, index) => {
      const range = stats.max[index] - stats.min[index];
      return range > 0 ? (value - stats.mean[index]) / range : 0;
    });
  }

  // Витяг ознак для прогнозування
  extractFeaturesForPrediction(date, lastRecord) {
    const dayOfWeek = date.getDay();
    const month = date.getMonth();
    const isWeekend = [0, 6].includes(dayOfWeek) ? 1 : 0;
    const season = MLDataPreprocessor.getSeason(month);

    return [
      dayOfWeek / 6,
      month / 11,
      isWeekend,
      season / 3,
      lastRecord.shiftFeatures.shiftType,
      lastRecord.shiftFeatures.operatorsCount / 5,
      lastRecord.shiftFeatures.shiftDuration / 24,
      lastRecord.productionFeatures.activeProgramsCount / 10,
      lastRecord.productionFeatures.rejectionRate / 100
    ];
  }

  // Розрахунок впевненості для TensorFlow моделі
  calculateTFConfidence(daysAhead) {
    const baseConfidence = this.modelMetrics.accuracy;
    const timeDecay = daysAhead * 2; // -2% за кожен день (краще ніж у простої моделі)
    const dataQualityBonus = Math.min(20, (this.trainingData.length / 50) * 20); // Бонус за якість даних
    
    return Math.max(40, baseConfidence - timeDecay + dataQualityBonus);
  }

  // Статистика моделі
  getModelStats() {
    const baseStats = {
      ...this.modelMetrics,
      trainingDataSize: this.trainingData.length,
      featuresUsed: 9,
      lastPrediction: new Date(),
      usingTensorFlow: this.isUsingTF
    };

    if (this.isUsingTF && this.model) {
      baseStats.modelArchitecture = 'Sequential_64_32_16_1';
      baseStats.optimizer = 'Adam';
      baseStats.backend = this.tf.getBackend();
    }

    return baseStats;
  }

  // Очищення пам'яті TensorFlow
  dispose() {
    if (this.model) {
      this.model.dispose();
    }
  }

  // Успадковані методи з простої моделі (для сумісності)
  prepareTrainingData() {
    const features = [];
    const targets = [];

    const sortedData = [...this.trainingData].sort((a, b) => a.timestamp - b.timestamp);

    for (let i = 0; i < sortedData.length; i++) {
      const current = sortedData[i];
      const featureVector = [
        current.timeFeatures.dayOfWeek,
        current.timeFeatures.month,
        current.timeFeatures.isWeekend,
        current.shiftFeatures.shiftType,
        current.shiftFeatures.operatorsCount,
        current.productionFeatures.activeProgramsCount,
        current.timeFeatures.season
      ];

      features.push(featureVector);
      targets.push(current.productionFeatures.totalProduction);
    }

    return { features, targets };
  }

  simpleLinearRegression(features, targets) {
    const model = {
      coefficients: this.calculateAverageCoefficients(features, targets),
      featureWeights: this.calculateFeatureWeights(features, targets),
      intercept: this.calculateIntercept(features, targets, this.calculateAverageCoefficients(features, targets)),
      type: 'linear_regression'
    };

    return model;
  }

  calculateAverageCoefficients(features, targets) {
    if (features.length === 0) return [0, 0, 0, 0, 0, 0, 0];
    
    const coefficients = [0, 0, 0, 0, 0, 0, 0];
    const featureSums = [0, 0, 0, 0, 0, 0, 0];
    const counts = [0, 0, 0, 0, 0, 0, 0];

    features.forEach(featureVector => {
      featureVector.forEach((value, index) => {
        featureSums[index] += value;
        counts[index]++;
      });
    });

    return featureSums.map((sum, index) => {
      const avg = counts[index] > 0 ? sum / counts[index] : 0;
      return avg * 0.1;
    });
  }

  calculateFeatureWeights(features, targets) {
    if (features.length === 0) return [1, 1, 1, 1, 1, 1, 1];
    
    const weights = [1, 1, 1, 1, 1, 1, 1];
    weights[0] = 1.5; // dayOfWeek
    weights[1] = 1.2; // month
    weights[2] = 1.3; // isWeekend
    weights[3] = 1.1; // shiftType
    weights[4] = 1.4; // operatorsCount
    weights[5] = 1.2; // activeProgramsCount
    weights[6] = 1.1; // season
    
    return weights;
  }

  calculateIntercept(features, targets, coefficients) {
    if (targets.length === 0) return 0;
    
    const targetAvg = targets.reduce((sum, target) => sum + target, 0) / targets.length;
    return targetAvg * 0.8;
  }

  calculateModelAccuracy(features, targets) {
    if (features.length === 0) return 0;
    
    let totalError = 0;
    let totalTarget = 0;

    features.forEach((feature, index) => {
      const prediction = this.predictWithModel(feature, this.model);
      const actual = targets[index];
      totalError += Math.abs(prediction - actual);
      totalTarget += actual;
    });

    const meanError = totalError / features.length;
    const meanTarget = totalTarget / features.length;
    
    if (meanTarget === 0) return 0;
    
    const accuracy = Math.max(0, 100 * (1 - (meanError / meanTarget)));
    return accuracy;
  }

  predictWithModel(features, model) {
    if (!model || !model.coefficients) {
      return this.fallbackSinglePrediction(features);
    }

    let prediction = model.intercept || 0;

    features.forEach((feature, index) => {
      const coefficient = model.coefficients[index] || 0;
      const weight = model.featureWeights?.[index] || 1;
      prediction += coefficient * feature * weight;
    });

    return Math.max(0, prediction);
  }

  predictSingleDaySimple(date, lastRecord) {
    const dayOfWeek = date.getDay();
    const month = date.getMonth();
    const seasonality = this.getSeasonalityFactor(dayOfWeek, month);
    const baseProduction = 1500;
    
    return Math.round(baseProduction * seasonality);
  }

  getSeasonalityFactor(dayOfWeek, month) {
    const baseFactors = {
      0: 0.7, 1: 0.9, 2: 1.0, 3: 1.1, 4: 1.2, 5: 1.3, 6: 0.8
    };

    let factor = baseFactors[dayOfWeek] || 1.0;

    if (month >= 5 && month <= 7) factor *= 1.2;
    if (month >= 11 || month <= 1) factor *= 0.9;

    return factor;
  }

  fallbackPrediction(days) {
    const validData = this.trainingData.filter(d => d.productionFeatures.totalProduction > 0);
    
    if (validData.length === 0) {
      return this.emergencyFallback(days);
    }

    const avgProduction = validData.reduce((sum, d) => 
      sum + d.productionFeatures.totalProduction, 0
    ) / validData.length;

    return Array.from({ length: days }, (_, i) => {
      const date = new Date();
      date.setDate(date.getDate() + i + 1);
      const dayOfWeek = date.getDay();
      const seasonality = this.getSeasonalityFactor(dayOfWeek, date.getMonth());
      
      return {
        date: date.toISOString().split('T')[0],
        predictedProduction: Math.round(avgProduction * seasonality),
        confidence: 60 - (i * 5),
        modelUsed: 'fallback_seasonal',
        seasonalityFactor: seasonality
      };
    });
  }

  emergencyFallback(days) {
    const baseProduction = 1500;
    
    return Array.from({ length: days }, (_, i) => ({
      date: new Date(Date.now() + (i + 1) * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      predictedProduction: baseProduction,
      confidence: 40 - (i * 3),
      modelUsed: 'emergency_fallback',
      note: 'Недостатньо даних для точного прогнозу'
    }));
  }

  fallbackSinglePrediction(features) {
    const [dayOfWeek, month] = features;
    const seasonality = this.getSeasonalityFactor(dayOfWeek, month);
    const baseProduction = 1500;
    
    return Math.round(baseProduction * seasonality);
  }

  getPredictionFeatures(date, lastRecord) {
    return {
      dayOfWeek: date.getDay(),
      month: date.getMonth(),
      isWeekend: [0, 6].includes(date.getDay()),
      lastOperatorsCount: lastRecord.shiftFeatures.operatorsCount,
      lastActivePrograms: lastRecord.productionFeatures.activeProgramsCount,
      season: MLDataPreprocessor.getSeason(date.getMonth())
    };
  }
}

export default MLPredictorTF;